import { ComponentPropsWithoutRef, Fragment, forwardRef, useEffect, useMemo, useState } from "react"
import { Link, useHydrating, useRoute } from "eddev/routing"
import { useAppData } from "eddev/hooks"
import { MenuItemFieldsFragment, MenuLocationEnum } from "../../types.graphql"
import { ARROW_RIGHT, CARET_DOWN, CARET_RIGHT } from "./Icons"
import { cn } from "@utils/tw"
import { FACEBOOK, LINKEDIN, X, YOUTUBE } from "@components/atoms/Icons"
import { useSocialLinks } from "@hooks/queries"
import { useNav } from "@hooks/useNav"

/**
 * Feel free to adjust this file to meet your needs! Keep in mind that you shouldn't
 * add any styling to this file, as it's meant to be a generic component.
 *
 * To style a menu, wrap it in a component that sets a `className` (via `cn()`)
 * and target the ul/li/a with descendant selectors — e.g. Navigation.tsx's
 * `NAV_LIST_BASE`/`MenuContainer`, or Footer.tsx's `LegalMenu`.
 */

type MenuItemProps = {
  item: MenuItemFieldsFragment
  accordion?: boolean
  depth: number
  open: number | null
  setOpen: any
  keyNo: number
  type?: "accordion"
}

function MenuItem(props: MenuItemProps) {
  const { item, type, depth, open, keyNo } = props

  const route = useRoute()
  const nav = useNav()

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    props.setOpen(keyNo === open ? null : keyNo)
  }

  const [hasChildren, setHasChildren] = useState<boolean>(false)
  useEffect(() => {
    if (props.item.childItems) {
      if (props.item.childItems.nodes.length > 0) setHasChildren(true)
    }
  }, [])

  if (item.menuItemType !== "custom" && !item.connectedNode) return null

  return (
    <>
      {item.url === route.pathname ? (
        <FakeLink
          data-href={item.url!}
          data-target={item.target!}
          className={`current-page ${keyNo === open ? `open` : ""}`}
          onClick={() => nav.setOpen(false)}
        >
          {item.label}

          {type === "accordion" && hasChildren ? (
            <button onClick={(e) => handleClick(e)}>{CARET_DOWN}</button>
          ) : (
            <>
              {depth === 0 && <>{ARROW_RIGHT}</>}
              {depth > 0 && type !== "accordion" && <>{CARET_RIGHT}</>}
            </>
          )}
        </FakeLink>
      ) : (
        <Link href={item.url!} target={item.target!} className={keyNo === open ? `open` : ``}>
          {item.label}

          {type === "accordion" && hasChildren ? (
            <button onClick={(e) => handleClick(e)}>{CARET_DOWN}</button>
          ) : (
            <>
              {depth === 0 && <>{ARROW_RIGHT}</>}
              {depth > 0 && type !== "accordion" && <>{CARET_RIGHT}</>}
            </>
          )}
        </Link>
      )}
    </>
  )
}

type MenuItemsProps = {
  items: MenuItemFieldsFragment[]
  className?: string
  depth: number
  type?: "accordion"
  showLevels: number | null
  hideLevels: number | null
  social?: boolean
}

function MenuItems(props: MenuItemsProps) {
  const [open, setOpen] = useState<number | null>(null)
  const hydrating = useHydrating()
  const socialLinks = useSocialLinks(undefined).data?.siteSettings?.themeSiteSettings?.socialMedia

  if (props.items) {
    if (props.showLevels !== null && props.hideLevels !== null) {
      return (
        <ul data-menu-depth={props.depth}>
          {props.items.map((item, i) => {
            if (item.menuItemType !== "custom" && !item.connectedNode) return null
            return (
              <Fragment key={item.label}>
                <li className={item.cssClasses?.length ? item.cssClasses.join(" ") : undefined}>
                  {props.hideLevels! < props.depth && (
                    <MenuItem
                      item={item}
                      depth={props.depth}
                      type={props.type ?? undefined}
                      open={open}
                      setOpen={setOpen}
                      keyNo={i}
                    />
                  )}
                  {item.childItems?.nodes?.length! > 0 && props.showLevels! + props.hideLevels! > props.depth && (
                    <MenuItems
                      depth={props.depth + 1}
                      items={item.childItems!.nodes as MenuItemFieldsFragment[]}
                      showLevels={props.showLevels}
                      hideLevels={props.hideLevels}
                      type={props.type ?? undefined}
                    />
                  )}
                </li>
                {!hydrating && props.social && props.items.length === i + 1 && socialLinks && (
                  <Social>
                    {socialLinks.facebook && (
                      <Link href={socialLinks.facebook} target="_blank">
                        {FACEBOOK}
                      </Link>
                    )}
                    {socialLinks.x && (
                      <Link href={socialLinks.x} target="_blank">
                        {X}
                      </Link>
                    )}
                    {socialLinks.linkedin && (
                      <Link href={socialLinks.linkedin} target="_blank">
                        {LINKEDIN}
                      </Link>
                    )}
                    {socialLinks.youtube && (
                      <Link href={socialLinks.youtube} target="_blank">
                        {YOUTUBE}
                      </Link>
                    )}
                  </Social>
                )}
              </Fragment>
            )
          })}
        </ul>
      )
    }

    if (props.showLevels !== null) {
      return (
        <ul data-menu-depth={props.depth}>
          {props.items.map((item, i) => {
            if (item.menuItemType !== "custom" && !item.connectedNode) return null
            return (
              <Fragment key={item.label}>
                <li className={item.cssClasses?.length ? item.cssClasses.join(" ") : undefined}>
                  <MenuItem
                    item={item}
                    depth={props.depth}
                    type={props.type ?? undefined}
                    open={open}
                    setOpen={setOpen}
                    keyNo={i}
                  />
                  {item.childItems?.nodes?.length! > 0 && props.showLevels! > props.depth && (
                    <MenuItems
                      depth={props.depth + 1}
                      items={item.childItems!.nodes as MenuItemFieldsFragment[]}
                      showLevels={props.showLevels}
                      hideLevels={props.hideLevels}
                    />
                  )}
                  {!hydrating && props.social && props.items.length === i + 1 && socialLinks && (
                    <Social>
                      {socialLinks.facebook && (
                        <Link href={socialLinks.facebook} target="_blank">
                          {FACEBOOK}
                        </Link>
                      )}
                      {socialLinks.x && (
                        <Link href={socialLinks.x} target="_blank">
                          {X}
                        </Link>
                      )}
                      {socialLinks.linkedin && (
                        <Link href={socialLinks.linkedin} target="_blank">
                          {LINKEDIN}
                        </Link>
                      )}
                      {socialLinks.youtube && (
                        <Link href={socialLinks.youtube} target="_blank">
                          {YOUTUBE}
                        </Link>
                      )}
                    </Social>
                  )}
                </li>
              </Fragment>
            )
          })}
        </ul>
      )
    }

    if (props.hideLevels !== null) {
      return (
        <ul data-menu-depth={props.depth}>
          {props.items.map((item, i) => {
            if (item.menuItemType !== "custom" && !item.connectedNode) return null
            return (
              <Fragment key={item.label}>
                <li className={item.cssClasses?.length ? item.cssClasses.join(" ") : undefined}>
                  {props.hideLevels! < props.depth && (
                    <MenuItem
                      item={item}
                      depth={props.depth}
                      type={props.type ?? undefined}
                      open={open}
                      setOpen={setOpen}
                      keyNo={i}
                    />
                  )}
                  {item.childItems?.nodes?.length! > 0 && (
                    <MenuItems
                      depth={props.depth + 1}
                      items={item.childItems!.nodes as MenuItemFieldsFragment[]}
                      showLevels={props.showLevels}
                      hideLevels={props.hideLevels}
                      type={props.type ?? undefined}
                    />
                  )}
                  {!hydrating && props.social && props.items.length === i + 1 && socialLinks && (
                    <Social>
                      {socialLinks.facebook && (
                        <Link href={socialLinks.facebook} target="_blank">
                          {FACEBOOK}
                        </Link>
                      )}
                      {socialLinks.x && (
                        <Link href={socialLinks.x} target="_blank">
                          {X}
                        </Link>
                      )}
                      {socialLinks.linkedin && (
                        <Link href={socialLinks.linkedin} target="_blank">
                          {LINKEDIN}
                        </Link>
                      )}
                      {socialLinks.youtube && (
                        <Link href={socialLinks.youtube} target="_blank">
                          {YOUTUBE}
                        </Link>
                      )}
                    </Social>
                  )}
                </li>
              </Fragment>
            )
          })}
        </ul>
      )
    }

    return (
      <ul data-menu-depth={props.depth}>
        {props.items.map((item, i) => {
          if (item.menuItemType !== "custom" && !item.connectedNode) return null
          return (
            <Fragment key={item.label}>
              <li className={item.cssClasses?.length ? item.cssClasses.join(" ") : undefined}>
                <MenuItem
                  item={item}
                  depth={props.depth}
                  type={props.type ?? undefined}
                  open={open}
                  setOpen={setOpen}
                  keyNo={i}
                />
                {item.childItems?.nodes?.length! > 0 && (
                  <MenuItems
                    depth={props.depth + 1}
                    items={item.childItems!.nodes as MenuItemFieldsFragment[]}
                    showLevels={props.showLevels}
                    hideLevels={props.hideLevels}
                    type={props.type ?? undefined}
                  />
                )}
                {!hydrating && props.social && props.items.length === i + 1 && socialLinks && (
                  <Social>
                    {socialLinks.facebook && (
                      <Link href={socialLinks.facebook} target="_blank">
                        {FACEBOOK}
                      </Link>
                    )}
                    {socialLinks.x && (
                      <Link href={socialLinks.x} target="_blank">
                        {X}
                      </Link>
                    )}
                    {socialLinks.linkedin && (
                      <Link href={socialLinks.linkedin} target="_blank">
                        {LINKEDIN}
                      </Link>
                    )}
                    {socialLinks.youtube && (
                      <Link href={socialLinks.youtube} target="_blank">
                        {YOUTUBE}
                      </Link>
                    )}
                  </Social>
                )}
              </li>
            </Fragment>
          )
        })}
      </ul>
    )
  }
  return null
}

type Props = {
  location: keyof typeof MenuLocationEnum
  className?: string
  type?: "accordion"
  showLevels?: number
  hideLevels?: number
  setTabbedMenu?: React.Dispatch<React.SetStateAction<number>>
  tabbedMenu?: number
  social?: boolean
}

export function Menu(props: Props) {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const menu = menus?.find((menu) => menu?.locations?.includes(MenuLocationEnum[props.location]))
  if (env.dev) {
    useEffect(() => {
      if (!menu) {
        console.error(`No menu for location "${props.location}".`)
      }
    }, [menu, props.location])
  }

  const menuItems = useMemo(() => {
    const filteredList = menu?.menuItems?.nodes.filter((p) => !p.parentId) as MenuItemFieldsFragment[]
    return filteredList
  }, [props])

  if (menuItems) {
    return (
      <MenuItems
        className={props.className}
        depth={0}
        items={menuItems}
        type={props.type ?? undefined}
        showLevels={props.showLevels ?? null}
        hideLevels={props.hideLevels ?? null}
        social={props.social}
      />
    )
  } else {
    return null
  }
}

const Social = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-full justify-start mt-4",
      "[&_a]:transition-colors [&_a]:ease-movement [&_a]:duration-300 [&_a]:rounded-[6px]",
      "[&_a]:!p-[4px] [&_a]:!m-0 [&_a]:!mr-2 [&_a]:!w-[32px] [&_a]:!h-[32px]",
      "[&_a_svg]:!w-[32px] [&_a_svg]:!h-[32px]",
      "[&_a:hover]:bg-white [&_a:hover]:text-black",
      className,
    )}
    {...props}
  />
))
Social.displayName = "Social"

const FakeLink = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(({ className, ...props }, ref) => (
  <span ref={ref} className={className} {...props} />
))
FakeLink.displayName = "FakeLink"
